<template>
  <div>
    <v-overlay :value="isProcessing">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      />
    </v-overlay>
    <VerifyCodeDialog
      :verificationType="emailVerificationType"
      :contactInfo="email"
      :showDialog="showVerifyEmail"
      :onClose="() => showVerifyEmail = false"
      :onSuccess="() => { showVerifyEmail = false; verifiedEmail = true; }"
      :challengeId="challengeId"
      :onVerify="verifyRecoveryCode"
    />
    <UniqueUsernameDialog
      :showDialog="showNonUniqueUsername"
      :username="username"
      :onCheck="checkUsername"
      :onClose="() => showNonUniqueUsername = false"
    />
    <v-row justify="center">
      <v-col lg="6" md="5" class="pb-0 hidden-sm-and-down">
        <v-img class="sticky-image" height="100vh" src="../../assets/register-dog.png" />
      </v-col>
      <v-col class="register" lg="6" md="7" sm="12">
        <div class="top-icons">
          <a
            class="back-icon"
            v-on:click="goBack"
          >
            <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back
          </a>
        </div>
        <v-alert
          width="100%"
          v-model="isError"
          dismissible
          type="error"
        >{{ errorMessage }}</v-alert>
        <h1 id="title">Account Recovery</h1>
        <p v-if="!verifiedEmail">
          Please <a v-on:click="startVerification">verify</a> your email before we start
        </p>
        <br v-else />
        <v-form v-model="isValid" class="register-form">
          <v-text-field
            outlined
            required
            disabled
            label="Email"
            type="email"
            v-model="email"
          ></v-text-field>
          <v-text-field
            outlined
            required
            label="Create Username"
            type="text"
            v-model="username"
            :rules="[(v) => !!v || 'Username is required']"
            :disabled="!verifiedEmail"
          ></v-text-field>
          <v-text-field
            outlined
            required
            label="Create Password"
            type="password"
            v-model="password1"
            :rules="passwordRules"
            :disabled="!verifiedEmail"
          ></v-text-field>
          <PasswordRules v-if="verifiedEmail" :password="password1" />
          <v-text-field
            outlined
            required
            label="Confirm Password"
            type="password"
            v-model="password2"
            :rules="[
              (v) => !!v || 'Confirm password is required',
              (v) => v === password1 || 'Passwords do not match'
            ]"
            :disabled="!verifiedEmail"
          ></v-text-field>
        </v-form>
        <v-btn
          :dark="isValid && verifiedEmail"
          large
          color="primary"
          v-on:click="submit()"
          :disabled="!isValid || !verifiedEmail"
        >SUBMIT</v-btn>
        <div class="footer-container">
          <v-img height="2rem" contain src="../../assets/logo.svg" />
          <footer-text />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import decodeToken from "@/helpers/decode-token";

import passwordValidators from '@/helpers/validate-password';

import authenticationService from '@/services/authenticationService';
import { VerificationType } from "@/services/codeVerificationService";
import personService from '@/services/portal/personService';

import FooterText from '@/components/common/FooterText.vue';
import VerifyCodeDialog from '@/components/common/VerifyCodeDialog.vue';
import UniqueUsernameDialog from '@/components/common/UniqueUsernameDialog.vue';
import PasswordRules from '@/components/common/PasswordRules.vue';
import { getErrorMessage } from '@/services/errorHandler';

export default Vue.extend({
  components: { FooterText, VerifyCodeDialog, UniqueUsernameDialog, PasswordRules },
  name: 'RecoverAccount',
  data() {
    const recoverInfo = this.$store.state.recoverInfo;

    return {
      isValid: false,
      isError: false,
      isProcessing: false,
      errorMessage: '',

      showVerifyEmail: false,
      verifiedEmail: false,
      challengeId: '',
      recoveryToken: '',

      showNonUniqueUsername: false,

      email: recoverInfo?.email,
      username: recoverInfo?.username,
      password1: recoverInfo?.password,
      password2: recoverInfo?.confirmPassword,
      passwordRules: passwordValidators,
    }
  },
  computed: {
    emailVerificationType() {
      return VerificationType.Email;
    },
  },
  methods: {
    goBack() { this.$router.back(); },
    startVerification: async function() {
      this.isProcessing = true;
      this.isError = false;
      try {
        const response = await authenticationService.startAccountRecovery(this.email);
        this.challengeId = response.challengeId;
        this.showVerifyEmail = true;
      } catch (error) {
        this.isError = true;
        this.errorMessage = 'Account is not recoverable with this email.';
      } finally {
        this.isProcessing = false;
      }
    },
    verifyRecoveryCode: async function(challengeId: string, code: string) {
      const response = await personService.verifyAccountRecovery(challengeId, code);
      if (response.success) this.recoveryToken = response.recoveryToken;
      return response;
    },
    processRecovery: async function (res: any) {
      const decoded: any = decodeToken(res.token);
      const event = new CustomEvent("userLoaded", {
          detail: { personId: decoded.nameid, token: res.token },
      });
      localStorage.setItem("userToken", res.token);
      window.dispatchEvent(event);
    },
    checkUsername: async function(newUsername?: string) {
      if (!!newUsername) this.username = newUsername;

      try {
        const uniqueUsername = await authenticationService.uniqueUsername(this.username);

        this.showNonUniqueUsername = !uniqueUsername;

        return uniqueUsername;
      } catch (error) {
        this.isError = true;
        this.errorMessage = getErrorMessage(error);
        return false;
      }
    },
    submit: async function() {
      this.isProcessing = true;

      try {
        const uniqueUsername = await this.checkUsername();

        if (uniqueUsername && this.verifiedEmail) {
          const response = await authenticationService.recoverAccount({
          email: this.email,
          username: this.username,
          password1: this.password1,
          password2: this.password2,
          recoveryToken: this.recoveryToken,
        });

          if (response.errors != null) {
            this.isError = true;
            this.errorMessage = response.errors ? response.errors.join(',') : 'An Unknown Error Occured';
          } else if (response.frozen) {

            this.$store.commit('changeParentVerification', {
              juniorId: response.personId,
              firstName: response.firstName,
              lastName: response.lastName
            });
            this.$router.push('/parent-member');
          } else {
            await this.processRecovery(response);

            this.$router.push('/agreements');
          }
        }
      } catch (error) {
        this.isError = true;
        this.errorMessage = getErrorMessage(error);
      } finally {
        this.isProcessing = false;
      }
    },
  },
});
</script>
